import { useMemo, useState } from "react"
import {
	Alert,
	Badge,
	Box,
	Button,
	Component,
	Divider,
	Icons,
	Inline,
	Select,
	Spinner,
	Stack,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Text,
	useBreakpoints,
} from "@sembark-travel/ui-base"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import {
	DatePickerField,
	Form,
	GetFieldValue,
	validateFormValues,
} from "@sembark-travel/ui-form"
import useSWR from "swr"
import { formatDate } from "@sembark-travel/datetime-utils"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	diffMarkdownText,
	diffTone,
	type TDiffSide,
	type TDiffStatus,
	type TDiffTone,
	type TPolicyTextDiffEntry,
} from "./policyDiffUtils"
import {
	dateRangeSchema,
	findOverlappingDateRange,
	formatDateRange,
	type TDateRange,
} from "./policies"

// ─── Version History / Diff UI ───────────────────────────────────────────────

export type TPolicyVersionItem = {
	id: number
	version: number
	created_at?: string
	created_by?: { name?: string } | null
}

export type TVersionHistoryDialogProps<
	TVersionData extends {
		additional_info: string | null
		created_by?: { name?: string } | null
	},
> = {
	detailId: number
	currentVersionId: number
	currentVersion: number
	children: (props: { open: () => void }) => React.ReactNode
	/** SWR key for the paginated versions list */
	versionsKey: (detailId: number) => string
	/** Fetch the paginated version list (only id/version/created_at required) */
	fetchVersions: (detailId: number) => Promise<{ data: TPolicyVersionItem[] }>
	/** SWR key for a single version detail */
	versionKey: (versionId: number) => string
	/** Fetch a single version by id */
	fetchVersion: (versionId: number) => Promise<TVersionData>
	/**
	 * Render the domain-specific diff entries.
	 * Called only after both `previous` and `current` are loaded.
	 * Return `hasChanges` so the panel can decide whether to show
	 * "No changes between these versions."
	 */
	renderDiffEntries: (
		previous: TVersionData,
		current: TVersionData
	) => { entries: React.ReactNode; hasChanges: boolean }
}

export function VersionHistoryDialog<
	TVersionData extends {
		additional_info: string | null
		created_by?: { name?: string } | null
	},
>({
	detailId,
	currentVersionId,
	currentVersion,
	children,
	versionsKey,
	fetchVersions,
	versionKey,
	fetchVersion,
	renderDiffEntries,
}: TVersionHistoryDialogProps<TVersionData>) {
	return (
		<Component initialState={false}>
			{({ state, setState }) => (
				<>
					{children({ open: () => setState(true) })}
					{state ? (
						<Dialog
							open={state}
							onClose={() => setState(false)}
							title="Version History"
							xl
						>
							<VersionHistory
								detailId={detailId}
								currentVersionId={currentVersionId}
								currentVersion={currentVersion}
								versionsKey={versionsKey}
								fetchVersions={fetchVersions}
								versionKey={versionKey}
								fetchVersion={fetchVersion}
								renderDiffEntries={renderDiffEntries}
							/>
						</Dialog>
					) : null}
				</>
			)}
		</Component>
	)
}

function VersionHistory<
	TVersionData extends {
		additional_info: string | null
		created_by?: { name?: string } | null
	},
>({
	detailId,
	currentVersionId,
	currentVersion,
	versionsKey,
	fetchVersions,
	versionKey,
	fetchVersion,
	renderDiffEntries,
}: Omit<TVersionHistoryDialogProps<TVersionData>, "children">) {
	const {
		data: versions,
		error,
		isLoading,
	} = useSWR(versionsKey(detailId), () => fetchVersions(detailId))

	const sortedVersions = useMemo(
		() =>
			versions?.data
				? [...versions.data].sort((a, b) => b.version - a.version)
				: [],
		[versions]
	)

	const [selectedVersionId, setSelectedVersionId] = useState<number | null>(
		null
	)

	const activeVersionId =
		selectedVersionId ??
		sortedVersions.find((v) => v.id !== currentVersionId)?.id ??
		null
	const activeVersion = sortedVersions.find((v) => v.id === activeVersionId)

	if (isLoading) {
		return (
			<Box padding="4" textAlign="center">
				<Spinner />
			</Box>
		)
	}

	if (error) {
		return (
			<Box padding="4">
				<Alert status="error">
					{(error as Error).message ||
						"Failed to load the versions. Please try again."}
				</Alert>
			</Box>
		)
	}

	if (!sortedVersions.length) {
		return (
			<Box padding="4">
				<Text color="muted">No versions yet.</Text>
			</Box>
		)
	}

	if (sortedVersions.length < 2) {
		return (
			<Box padding="4">
				<Text color="muted">Only one version exists. Nothing to compare.</Text>
			</Box>
		)
	}

	return (
		<VersionHistoryLayout
			sortedVersions={sortedVersions}
			activeVersionId={activeVersionId}
			activeVersion={activeVersion}
			currentVersionId={currentVersionId}
			currentVersion={currentVersion}
			setSelectedVersionId={setSelectedVersionId}
			versionKey={versionKey}
			fetchVersion={fetchVersion}
			renderDiffEntries={renderDiffEntries}
		/>
	)
}

function VersionHistoryLayout<
	TVersionData extends {
		additional_info: string | null
		created_by?: { name?: string } | null
	},
>({
	sortedVersions,
	activeVersionId,
	activeVersion,
	currentVersionId,
	currentVersion,
	setSelectedVersionId,
	versionKey,
	fetchVersion,
	renderDiffEntries,
}: {
	sortedVersions: TPolicyVersionItem[]
	activeVersionId: number | null
	activeVersion: TPolicyVersionItem | undefined
	currentVersionId: number
	currentVersion: number
	setSelectedVersionId: (id: number) => void
	versionKey: (id: number) => string
	fetchVersion: (id: number) => Promise<TVersionData>
	renderDiffEntries: (
		previous: TVersionData,
		current: TVersionData
	) => { entries: React.ReactNode; hasChanges: boolean }
}) {
	const { xs, sm } = useBreakpoints()
	const isMobile = xs || sm

	const diffPanel =
		activeVersionId && activeVersion ? (
			<PolicyDiffPanel
				previousVersionId={activeVersionId}
				previousVersion={activeVersion.version}
				currentVersionId={currentVersionId}
				versionKey={versionKey}
				fetchVersion={fetchVersion}
				renderDiffEntries={renderDiffEntries}
			/>
		) : (
			<Text color="muted">Select a version to compare.</Text>
		)

	if (isMobile) {
		const selectableVersions = sortedVersions
			.filter((v) => v.id !== currentVersionId)
			.map((v) => ({
				id: v.id,
				name: `v${v.version}`,
				created_at: v.created_at,
			}))
		const selectedOption = selectableVersions.find(
			(o) => o.id === activeVersionId
		)
		return (
			<Stack gap="4">
				<Inline gap="2" alignItems="center" paddingX="4" paddingTop="4">
					<Select
						value={selectedOption}
						onChange={(option: (typeof selectableVersions)[number]) => {
							if (option) setSelectedVersionId(option.id)
						}}
						clearable={false}
						options={selectableVersions}
						optionRenderer={({ option }) => (
							<Stack>
								<Text>{option.name}</Text>
								{option.created_at ? (
									<Text fontSize="sm">
										{formatDate(option.created_at, "DD MMM, YYYY")}
									</Text>
								) : null}
							</Stack>
						)}
						style={{ maxWidth: "9rem" }}
					/>
					<Text color="muted">→</Text>
					<Badge accent>v{currentVersion} Current</Badge>
				</Inline>
				<Divider marginY="0" />
				<Box padding="4">{diffPanel}</Box>
			</Stack>
		)
	}

	return (
		<Tabs layout="row" collapseBelowXs={false} style={{ minHeight: "60vh" }}>
			<TabsList
				style={{ minWidth: "12rem", maxWidth: "14rem", padding: 0, rowGap: 0 }}
				roundedBottomLeft="lg"
				flexShrink="0"
			>
				<Box padding="6" paddingBottom="2">
					<Text fontWeight="semibold" color="muted" fontSize="sm">
						Versions
					</Text>
				</Box>
				{sortedVersions.map((version) => {
					const isCurrent = version.id === currentVersionId
					const isActive = version.id === activeVersionId
					return (
						<TabItem
							key={version.id}
							active={isActive}
							onClick={
								isCurrent ? undefined : () => setSelectedVersionId(version.id)
							}
							cursor={isCurrent ? "disabled" : "pointer"}
						>
							<Stack gap="1">
								<Inline gap="2" alignItems="center">
									<Text
										fontWeight="semibold"
										color={isActive ? "primary" : undefined}
									>
										v{version.version}
									</Text>
									{isCurrent ? <Badge accent>Current</Badge> : null}
								</Inline>
								{version.created_at ? (
									<Text fontSize="xs" color="muted" fontWeight="normal">
										{formatDate(version.created_at, "DD MMM YYYY, HH:mm")}
									</Text>
								) : null}
							</Stack>
						</TabItem>
					)
				})}
			</TabsList>

			<TabContent active flex="1" minWidth="0" overflow="auto" padding="4">
				<Inline gap="2" alignItems="center" paddingBottom="4">
					<Text fontWeight="semibold">v{activeVersion?.version}</Text>
					<Text color="muted">→</Text>
					<Badge accent>Current v{currentVersion}</Badge>
				</Inline>
				{diffPanel}
			</TabContent>
		</Tabs>
	)
}

function PolicyDiffPanel<
	TVersionData extends {
		additional_info: string | null
		created_by?: { name?: string } | null
	},
>({
	previousVersionId,
	previousVersion,
	currentVersionId,
	versionKey,
	fetchVersion,
	renderDiffEntries,
}: {
	previousVersionId: number
	previousVersion: number
	currentVersionId: number
	versionKey: (id: number) => string
	fetchVersion: (id: number) => Promise<TVersionData>
	renderDiffEntries: (
		previous: TVersionData,
		current: TVersionData
	) => { entries: React.ReactNode; hasChanges: boolean }
}) {
	const { data: current, error: currentError } = useSWR(
		versionKey(currentVersionId),
		() => fetchVersion(currentVersionId)
	)
	const { data: previous, error: previousError } = useSWR(
		versionKey(previousVersionId),
		() => fetchVersion(previousVersionId)
	)

	const textDiff = useMemo(
		() => diffMarkdownText(previous?.additional_info, current?.additional_info),
		[previous?.additional_info, current?.additional_info]
	)

	const error = currentError || previousError
	if (error) {
		return (
			<Alert status="error">
				{(error as Error).message ||
					"Failed to load the policy. Please try again."}
			</Alert>
		)
	}

	if (!previous || !current) {
		return (
			<Box padding="8" textAlign="center">
				<Spinner />
			</Box>
		)
	}

	const domainResult = renderDiffEntries(previous, current)

	const hasChanges =
		domainResult.hasChanges || (textDiff && textDiff.status !== "unchanged")

	return (
		<Stack gap="6">
			{!hasChanges ? (
				<Text color="muted">No changes between these versions.</Text>
			) : (
				<Stack gap="4">
					{domainResult.entries}
					{textDiff && textDiff.status !== "unchanged" ? (
						<AdditionalInfoDiffCard entry={textDiff} />
					) : null}
					{previous.created_by?.name ? (
						<Box paddingLeft="4">
							<Text fontSize="sm" color="muted">
								v{previousVersion} created by {previous.created_by.name}
							</Text>
						</Box>
					) : null}
				</Stack>
			)}
		</Stack>
	)
}

export function DiffPill({
	tone,
	fontSize,
	fontWeight,
	display,
	children,
}: {
	tone: TDiffTone
	fontSize?: React.ComponentProps<typeof Text>["fontSize"]
	fontWeight?: React.ComponentProps<typeof Text>["fontWeight"]
	display?: React.ComponentProps<typeof Box>["display"]
	children: React.ReactNode
}) {
	return (
		<Box
			display={display}
			paddingX="2"
			paddingY="1"
			rounded="md"
			bgColor={
				tone === "removed" ? "danger" : tone === "added" ? "success" : undefined
			}
		>
			<Text
				fontSize={fontSize}
				fontWeight={fontWeight ?? (tone ? "semibold" : "normal")}
				color={
					tone === "removed"
						? "danger"
						: tone === "added"
							? "success"
							: undefined
				}
				textDecoration={tone === "removed" ? "lineThrough" : undefined}
				style={{ whiteSpace: "pre-line" }}
			>
				{children}
			</Text>
		</Box>
	)
}

export function AdditionalInfoDiffCard({
	entry,
	title = "Additional information",
}: {
	entry: TPolicyTextDiffEntry
	title?: string
}) {
	return (
		<Box borderWidth="1" rounded="md" overflow="hidden">
			<Box display="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
				<Box padding="2" bgColor="subtle" fontWeight="semibold">
					<Text>{title}</Text>
				</Box>
				<Box padding="2" bgColor="subtle" fontWeight="semibold">
					<Text>{title}</Text>
				</Box>
				<ContentCell
					text={entry.previous}
					status={entry.status}
					side="previous"
				/>
				<ContentCell
					text={entry.current}
					status={entry.status}
					side="current"
				/>
			</Box>
		</Box>
	)
}

function ContentCell({
	text,
	status,
	side,
}: {
	text: string | null
	status: TDiffStatus
	side: TDiffSide
}) {
	const tone = diffTone(status, side)
	return (
		<Box paddingX="2" paddingY="3" borderTopWidth="1">
			{text ? (
				<Box
					paddingX="2"
					paddingY="2"
					rounded="md"
					bgColor={
						tone === "removed"
							? "danger"
							: tone === "added"
								? "success"
								: undefined
					}
					color={
						tone === "removed"
							? "danger"
							: tone === "added"
								? "success"
								: undefined
					}
					textDecoration={tone === "removed" ? "lineThrough" : undefined}
					fontSize="sm"
					style={{
						textDecoration: tone === "removed" ? "line-through" : undefined,
					}}
				>
					<Markdown>{text}</Markdown>
				</Box>
			) : (
				<Box paddingX="2" paddingY="1">
					<Text fontSize="sm" color="muted">
						-
					</Text>
				</Box>
			)}
		</Box>
	)
}

// ─── Date Range Rule UI ───────────────────────────────────────────────────────

// The date-range dialog's own form shape — a rule's validity window, which is
// all that dialog writes back.
export interface IDateRangeFormSchema {
	valid_from: TDateRange["valid_from"]
	valid_to: TDateRange["valid_to"]
}

export function DateRangeDialog({
	open,
	title,
	submitLabel,
	initialValues,
	minDate,
	existingDateRanges,
	onSuccess,
	onClose,
}: {
	open: boolean
	title: string
	submitLabel: string
	initialValues?: TDateRange
	minDate?: Date
	existingDateRanges?: Array<TDateRange>
	onSuccess: (dates: TDateRange) => void
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} title={title} sm>
			<Dialog.Body>
				<DateRangeForm
					submitLabel={submitLabel}
					initialValues={initialValues}
					minDate={minDate}
					existingDateRanges={existingDateRanges}
					onSubmit={onSuccess}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function DateRangeForm({
	submitLabel,
	initialValues,
	minDate,
	existingDateRanges,
	onSubmit,
}: {
	submitLabel: string
	initialValues?: IDateRangeFormSchema
	minDate?: Date
	existingDateRanges?: Array<TDateRange>
	onSubmit: (dates: IDateRangeFormSchema) => void
}) {
	const dateRangeValidationSchema = useMemo(
		() =>
			dateRangeSchema.test(
				"no-overlapping-date-ranges",
				"Date ranges must not overlap",
				function (dates) {
					const overlapping = findOverlappingDateRange(
						dates as TDateRange,
						existingDateRanges
					)
					if (!overlapping) return true
					return this.createError({ path: "date_range" })
				}
			),
		[existingDateRanges]
	)
	return (
		<Form<IDateRangeFormSchema>
			initialValues={initialValues || { valid_from: null, valid_to: null }}
			validate={validateFormValues(dateRangeValidationSchema)}
			onSubmit={async (dates) => onSubmit(dates)}
			subscription={{ values: true }}
		>
			{({ handleSubmit, values }) => {
				const overlappingRange = findOverlappingDateRange(
					values,
					existingDateRanges
				)
				return (
					<form
						noValidate
						onSubmit={(e) => {
							e.stopPropagation()
							handleSubmit(e)
						}}
					>
						<Stack gap="4">
							<Inline gap="4" alignItems="end">
								<Box flex="1">
									<DatePickerField
										label="Start date"
										name="valid_from"
										dateFormat="DD MMM, YYYY"
										min={minDate}
									/>
								</Box>
								<Box paddingBottom="3">
									<Icons.ArrowLeft rotate="180" />
								</Box>
								<Box flex="1">
									<GetFieldValue<TDateRange["valid_from"]> name="valid_from">
										{({ value }) => (
											<DatePickerField
												label="End date"
												name="valid_to"
												dateFormat="DD MMM, YYYY"
												min={value || minDate}
											/>
										)}
									</GetFieldValue>
								</Box>
							</Inline>
							{overlappingRange ? (
								<Alert status="error">
									{`Overlaps with the ${formatDateRange(
										overlappingRange.valid_from,
										overlappingRange.valid_to
									)} rule.`}
								</Alert>
							) : null}
						</Stack>
						<Divider marginY="4" />
						<Inline justifyContent="end">
							<Button type="submit" level="primary">
								{submitLabel}
							</Button>
						</Inline>
					</form>
				)
			}}
		</Form>
	)
}

export function AddDateRangeRuleButton({
	existingDateRanges,
	onAdd,
}: {
	existingDateRanges?: Array<TDateRange>
	onAdd: (dates: TDateRange) => void
}) {
	const [isAdding, startAdding, stopAdding] = useDialog()
	return (
		<>
			<Inline>
				<Button level="secondary" onClick={startAdding}>
					<Icons.Plus /> Add Date Specific Rules
				</Button>
			</Inline>
			<DateRangeDialog
				open={isAdding}
				title="Add Date Specific Rules"
				submitLabel="Add"
				minDate={new Date()}
				existingDateRanges={existingDateRanges}
				onSuccess={(dates) => {
					onAdd(dates)
					stopAdding()
				}}
				onClose={stopAdding}
			/>
		</>
	)
}
